import React from 'react';
import { Flex, Card, Button, Pagination, Empty, Grid } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ChannelCard from '../ChannelCard/ChannelCard';
import ChannelCardSkeleton from '../ChannelCardSkeleton/ChannelCardSkeleton';
import SharedFilterBar from '../../Shared/Filters/SharedFilterBar';
import { channelFilterFields, channelSortFields } from '../../Shared/Filters/filterConfig';
import useFilterOptionFields, { FILTER_OPTION_RESOURCE } from '../../Shared/Filters/useFilterOptionFields';
import PageToolbar from '../../Shared/PageToolbar';

const { useBreakpoint } = Grid;

const CHANNEL_FILTER_OPTION_RESOURCES = {
    agent_id: FILTER_OPTION_RESOURCE.AGENT,
    credential_id: FILTER_OPTION_RESOURCE.CREDENTIAL
};

const ChannelGrid = ({
    channels,
    loading,
    isInitialLoading,
    pagination,
    searchString,
    filters,
    sorter,
    onTableChange,
    onSearch,
    onFilterChange,
    onAction,
    onAdd,
    getHighlightClass,
    softRefreshKey,
    deleteLoading,
    updateLoading,
}) => {
    const { t } = useTranslation();
    const screens = useBreakpoint() || {};
    const isMobile = !!screens?.xs;
    const filterFields = useFilterOptionFields(channelFilterFields, CHANNEL_FILTER_OPTION_RESOURCES);

    const handlePaginationChange = (page, pageSize) => {
        onTableChange(
            { current: page, pageSize },
            {},
            { field: sorter.field, order: sorter.order }
        );
    };

    const handleSortChange = ({ sort_by, sort_direction }) => {
        const order = sort_direction === 'asc' ? 'ascend' : sort_direction === 'desc' ? 'descend' : null;
        onTableChange(
            pagination,
            {},
            { field: sort_by, order }
        );
    };

    return (
        <div style={{ padding: 0 }}>
            <PageToolbar
                title={t('channels')}
                style={{ marginBottom: 16 }}
                actions={
                    <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={onAdd}
                    >
                        {!isMobile && t('add_channel')}
                    </Button>
                }
                search={{
                    placeholder: t('search_channels'),
                    value: searchString,
                    onChange: (e) => onSearch(e.target.value),
                    allowClear: true,
                    desktopWidth: 300,
                }}
                filters={
                    <SharedFilterBar
                        fields={filterFields}
                        value={filters}
                        onChange={onFilterChange}
                        sortFields={channelSortFields}
                        sortValue={{ sort_by: sorter.field, sort_direction: sorter.order }}
                        onSortChange={handleSortChange}
                    />
                }
            />

            {(isInitialLoading || loading) ? (
                <Flex justify="center" gap={16} wrap>
                    {[...Array(8)].map((_, index) => (
                        <ChannelCardSkeleton key={index} />
                    ))}
                </Flex>
            ) : channels.length === 0 ? (
                <Card>
                    <Empty description={t('no_channels_found')} />
                </Card>
            ) : (
                <>
                    <Flex justify="center" gap={16} wrap>
                        {channels.map((channel) => (
                            <ChannelCard
                                key={channel.id || channel.external_channel_id}
                                channel={channel}
                                onAction={onAction}
                                deleteLoading={deleteLoading}
                                updateLoading={updateLoading}
                                className={getHighlightClass ? getHighlightClass(channel.id, 'socket-highlight-target-card') : ''}
                            />
                        ))}
                    </Flex>

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                        <Pagination
                            size="small"
                            simple={isMobile}
                            current={pagination.current}
                            pageSize={pagination.pageSize}
                            total={pagination.total}
                            onChange={handlePaginationChange}
                            showSizeChanger={!isMobile}
                            showQuickJumper={!isMobile}
                            pageSizeOptions={['10', '25', '50', '100']}
                            showTotal={(total, range) =>
                                `${range?.[0] || 0}-${range?.[1] || 0} ${t('of')} ${total} ${t('items')}`
                            }
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default ChannelGrid;
